import { useAuth } from '@wui/common-library'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { useLogoutHook } from '@/features/logout'

import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { Locales, useLocalization } from '@/shared/composables/localization'
import { UserRoles } from '@/shared/types/router'
import { pathNames } from '@/shared/utils/constants'

export const beforeEachHook = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  const { meta: { roles } = {}, query: { rt, lang } = {}, name, fullPath } = to

  const { fetchResourcesIfNeed } = useLocalization()

  await fetchResourcesIfNeed(name as string)
  const { getField } = useUserData()
  let userRole: UserRoles = getField(UserFields.USER_ROLE) ?? UserRoles.ANONYMOUS
  const { isAuthenticated } = useAuth()
  if (userRole === 'subscriber' && !isAuthenticated.value) {
    useLogoutHook().logout()
    userRole = UserRoles.ANONYMOUS
  }
  const accessAvailable = Array.isArray(roles) && roles.includes(userRole)

  /**
   * This block is for redirection to loginByToken page for support role
   * TODO WUI-1302 remove after RTU MOA Panel is released
   * https://jira.satel.org/browse/WUI-1302
   *
   */
  const redirectFromAdminPanel = rt && name === pathNames.MAIN
  if (redirectFromAdminPanel) {
    if (lang) {
      localStorage.setItem('supportLang', lang as string)
      const { setLocale } = useLocalization()
      setLocale(lang as Locales)
    }
    next({ name: pathNames.TOKEN_LOGIN, params: { accessToken: (rt as string) }})
  } else if (userRole === UserRoles.ANONYMOUS) {
    anonymousHandler(accessAvailable, next, fullPath, to)
  } else if (userRole === UserRoles.SUBSCRIBER) {
    subscriberHandler(accessAvailable, next, to)
  } else if (userRole === UserRoles.GUEST) {
    guestHandler(accessAvailable, next)
  } else if (userRole === UserRoles.SUPPORT) {
    supportHandler(accessAvailable, next)
  } else {
    next({ name: pathNames.LOGIN })
  }
}

const isInternalConferenceLinkRoute = (to: RouteLocationNormalized): boolean => {
  return to.name === pathNames.PREPARE_TO_CALL
}

const anonymousHandler = (
  accessAvailable: boolean,
  next: NavigationGuardNext,
  fullPath: string,
  to: RouteLocationNormalized,
) => {
  if (accessAvailable) {
    next()
    return
  }

  if (isInternalConferenceLinkRoute(to)) {
    next({
      name: pathNames.CONFERENCE_INTERNAL_LINK_DENIED,
      query: { callbackUrl: fullPath },
    })
    return
  }

  if (fullPath !== '/') {
    next({
      name: pathNames.LOGIN,
      query: { callbackUrl: fullPath },
    })
    return
  }

  next({ name: pathNames.LOGIN })
}

const subscriberHandler = (accessAvailable: boolean, next: NavigationGuardNext, to: RouteLocationNormalized) => {
  if (accessAvailable) {
    next()
  } else {
    const query: { sessionId?: string, inviteToken?: string } = {}
    if (to.query.sessionId) {
      query.sessionId = to.query.sessionId as string
    }
    if (to.params.inviteToken) {
      query.inviteToken = to.params.inviteToken as string
    }
    next({
      name: pathNames.MAIN,
      query,
    })
  }
}

const guestHandler = (accessAvailable: boolean, next: NavigationGuardNext) => {
  const { loadAuthGuestData, logout } = useGuestAuth()
  loadAuthGuestData()
  if (accessAvailable) {
    next()
  } else {
    logout()
    next({ name: pathNames.LOGIN })
  }
}

const supportHandler = (accessAvailable: boolean, next: NavigationGuardNext) => {
  if (accessAvailable) {
    next()
  } else {
    const { logout } = useLogoutHook()
    logout()
  }
}
